import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Lock } from 'lucide-react';
import { WaydevLogo } from './WaydevLogo';
const STORAGE_KEY = 'waydev-deck-unlocked';
const PASSWORD = 'AGI';
interface PasswordGateProps {
  children: React.ReactNode;
}
export function PasswordGate({
  children
}: PasswordGateProps) {
  const [unlocked, setUnlocked] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const [checked, setChecked] = useState(false);
  // Restore unlock state from sessionStorage
  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === 'true') {
        setUnlocked(true);
      }
    } catch {
      // sessionStorage may be unavailable
    }
    setChecked(true);
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim().toUpperCase() === PASSWORD) {
      try {
        sessionStorage.setItem(STORAGE_KEY, 'true');
      } catch {
        // ignore
      }
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
      setValue('');
    }
  };
  // Avoid flash of gate before sessionStorage check completes
  if (!checked) {
    return <div className="w-full h-screen bg-black" data-id="element-53" />;
  }
  if (unlocked) return <>{children}</>;
  return <div className="relative w-full h-screen bg-black text-white flex flex-col items-center justify-center px-6 overflow-hidden" data-id="element-54">
      {/* Subtle background grid lines for editorial feel */}
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
      backgroundSize: '80px 80px'
    }} data-id="element-55" />

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }} className="relative z-10 w-full max-w-md text-center" data-id="element-56">
        <div className="flex justify-center mb-12" data-id="element-57">
          <WaydevLogo className="h-16 md:h-20" data-id="element-58" />
        </div>

        <div className="flex items-center justify-center gap-2 mb-6 text-gray-400 text-xs uppercase tracking-[0.3em]" data-id="element-59">
          <Lock size={12} strokeWidth={1.5} data-id="element-60" />
          <span data-id="element-61">Confidential · Investor Access</span>
        </div>

        <h1 className="text-4xl md:text-5xl font-serif tracking-tight leading-[1.1] mb-3" data-id="element-62">
          Enter password to continue.
        </h1>
        <p className="text-sm text-gray-500 mb-12" data-id="element-63">
          This deck is shared privately with prospective partners.
        </p>

        <form onSubmit={handleSubmit} className="w-full" data-id="element-64">
          <div className={`flex items-stretch gap-3 border-b ${error ? 'border-white' : 'border-gray-700'} pb-3 transition-colors focus-within:border-white`} data-id="element-65">
            <input type="password" value={value} onChange={e => {
            setValue(e.target.value);
            if (error) setError(false);
          }} autoFocus autoComplete="off" placeholder="Password" aria-label="Deck password" className="flex-1 bg-transparent text-2xl font-light text-white placeholder:text-gray-700 focus:outline-none tracking-widest text-center" data-id="element-66" />
            <button type="submit" aria-label="Unlock deck" className="group flex items-center gap-2 px-4 text-sm uppercase tracking-widest text-gray-400 hover:text-white transition-colors" data-id="element-67">
              Enter
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" data-id="element-68" />
            </button>
          </div>

          <div className="h-6 mt-4" data-id="element-69">
            {error && <motion.p initial={{
            opacity: 0,
            y: -4
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-xs text-gray-400 uppercase tracking-widest" data-id="element-70">
                Incorrect password. Try again.
              </motion.p>}
          </div>
        </form>
      </motion.div>

      <div className="absolute bottom-8 left-0 right-0 text-center text-xs text-gray-600 uppercase tracking-widest" data-id="element-71">
        Waydev · Internal Brain
      </div>
    </div>;
}